/**
 * Notification Toast Component
 * 
 * Displays in-app toast notifications when FCM messages are received
 * Positioned in top-right corner with auto-dismiss
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFCMNotifications } from '../../Contexts/FCMNotificationContext';
import './style.css';

const NotificationToast = () => {
  const [notifications, setNotifications] = useState([]);
  const { onMessageReceived, isInitialized } = useFCMNotifications();
  const navigate = useNavigate();

  // Remove notification after timeout
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Add new notification
  const addNotification = useCallback((payload) => {
    const id = Date.now() + Math.random();
    const notification = payload?.notification || {};
    const data = payload?.data || {};

    const toast = {
      id,
      title: notification?.title || data?.subject || 'New Notification',
      body: notification?.body || data?.content || '',
      icon: notification?.icon || '/arisLogo.png',
      clickAction: data?.redirect_link || data?.click_action || '/dashboard',
      notificationType: data?.notification_type,
      notificationSubtype: data?.notification_subtype,
    };

    setNotifications((prev) => [...prev, toast]);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  }, [removeNotification]);

  // Handle notification click
  const handleClick = useCallback((toast) => {
    // Navigate to the click action
    if (toast.clickAction) {
      if (toast.clickAction.startsWith('http')) {
        // External URL - use window.location
        window.location.href = toast.clickAction;
      } else {
        // Internal route - use React Router
        navigate(toast.clickAction);
      }
    }
    // Remove the toast
    removeNotification(toast.id);
  }, [navigate, removeNotification]);

  // Listen for FCM messages via context (single source of truth)
  useEffect(() => {
    if (!isInitialized) return;

    const unsubscribe = onMessageReceived((payload) => {
      addNotification(payload);
    });

    return unsubscribe;
  }, [isInitialized, onMessageReceived, addNotification]);

  // Get icon based on notification type
  const getNotificationIcon = (type, subtype) => {
    // You can customize icons based on notification type
    switch (subtype) {
      case 'relationship_request':
        return '👥';
      case 'coaching_session':
        return '🎯';
      case 'assessment_reminder':
        return '📝';
      case 'subscription_reminder':
        return '💳';
      default:
        return '🔔';
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="notification-toast-container">
      {notifications.map((toast) => (
        <div
          key={toast.id}
          className="notification-toast"
          onClick={() => handleClick(toast)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClick(toast);
            }
          }}
        >
          <div className="notification-toast-icon">
            <div className="notification-toast-icon-bg">
              <span className="notification-toast-emoji">
                {getNotificationIcon(toast.notificationType, toast.notificationSubtype)}
              </span>
            </div>
          </div>
          <div className="notification-toast-content">
            <div className="notification-toast-title">{toast.title}</div>
            <div className="notification-toast-body">{toast.body}</div>
          </div>
          <button
            className="notification-toast-close"
            onClick={(e) => {
              e.stopPropagation();
              removeNotification(toast.id);
            }}
            aria-label="Close notification"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="notification-toast-arrow">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
